import { useState, useEffect } from 'react'
import { X, Search, Trash2, ExternalLink, CheckCircle2, XCircle, Copy, History, Link2, ListChecks, UserRound, Pencil, Save } from 'lucide-react'
import { fetchPerson, updatePerson, deletePerson, searchPerson, updateSearchResult, syncResearchTasks, updateResearchTask } from '../lib/api'
import { avatarColor, initials, formatDate, generateUsernames, STATUSES } from '../lib/utils'

export default function PersonDetail({ personId, onClose, onChanged }) {
  const [person, setPerson] = useState(null)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState(null)
  const [actionError, setActionError] = useState('')
  const [searchSummary, setSearchSummary] = useState(null)
  const [candidateAction, setCandidateAction] = useState('')
  const [reviewVisibleCount, setReviewVisibleCount] = useState(24)
  const [activeDetailTab, setActiveDetailTab] = useState('profile')
  const [showSensitiveSources, setShowSensitiveSources] = useState(false)
  const [copyNotice, setCopyNotice] = useState('')
  const [researchAction, setResearchAction] = useState('')
  const [editingProfile, setEditingProfile] = useState(false)
  const [savingProfile, setSavingProfile] = useState(false)
  const [editForm, setEditForm] = useState({})

  const load = async () => {
    setError(null)
    try {
      const data = await fetchPerson(personId)
      setPerson(data)
      setEditForm(personToForm(data))
    } catch (e) {
      setError(e.message)
    }
  }

  useEffect(() => { load() }, [personId])

  const handleStatusChange = async (status) => {
    setActionError('')
    try {
      await updatePerson(personId, { status })
      await load()
      onChanged?.()
    } catch (e) {
      setActionError(e.message)
    }
  }

  const handleSearch = async () => {
    setSearching(true)
    setActionError('')
    setSearchSummary(null)
    setReviewVisibleCount(24)
    try {
      const summary = await searchPerson(personId)
      setSearchSummary(summary)
      await load()
      onChanged?.()
    } catch (e) {
      setActionError(e.message)
    } finally {
      setSearching(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Diese Person wirklich löschen?')) return
    setActionError('')
    try {
      await deletePerson(personId)
      onChanged?.()
      onClose()
    } catch (e) {
      setActionError(e.message)
    }
  }

  const handleProfileSave = async () => {
    setSavingProfile(true)
    setActionError('')
    try {
      await updatePerson(personId, editForm)
      setEditingProfile(false)
      await load()
      onChanged?.()
    } catch (e) {
      setActionError(e.message)
    } finally {
      setSavingProfile(false)
    }
  }

  const handleProfileCancel = () => {
    setEditForm(personToForm(person))
    setEditingProfile(false)
    setActionError('')
  }

  const handleCandidateAction = async (resultId, action) => {
    setCandidateAction(`${resultId}:${action}`)
    setActionError('')
    try {
      await updateSearchResult(personId, resultId, action)
      await load()
      setSearchSummary(null)
      onChanged?.()
    } catch (e) {
      setActionError(e.message)
    } finally {
      setCandidateAction('')
    }
  }

  const handleResearchSync = async () => {
    setResearchAction('sync')
    setActionError('')
    try {
      await syncResearchTasks(personId)
      await load()
      onChanged?.()
    } catch (e) {
      setActionError(e.message)
    } finally {
      setResearchAction('')
    }
  }

  const handleResearchTaskAction = async (taskId, action) => {
    setResearchAction(`${taskId}:${action}`)
    setActionError('')
    try {
      await updateResearchTask(personId, taskId, action)
      await load()
      onChanged?.()
    } catch (e) {
      setActionError(e.message)
    } finally {
      setResearchAction('')
    }
  }

  const handleCopy = async (value) => {
    setCopyNotice('')
    try {
      await navigator.clipboard.writeText(value)
      setCopyNotice('Kopiert')
      setTimeout(() => setCopyNotice(''), 1400)
    } catch {
      setCopyNotice('Kopieren nicht möglich')
    }
  }

  if (error) return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-body">
          <div className="notice notice-error">
            <span>{error}</span>
            <button className="btn btn-ghost btn-sm" onClick={load}>Erneut laden</button>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={onClose}>Schließen</button>
          </div>
        </div>
      </div>
    </div>
  )
  if (!person) return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <span className="spinner" />
        </div>
      </div>
    </div>
  )

  const color = avatarColor(person.first_name + person.last_name)
  const usernames = generateUsernames(person.first_name, person.last_name, person.nickname)
  const reviewCandidates = person.review_candidates || []
  const visibleReviewCandidates = reviewCandidates.slice(0, reviewVisibleCount)
  const researchLinks = person.research_links || { dorks: [], sources: {}, summary: {} }
  const researchTasks = person.research_tasks || { items: [], counts: {} }
  const searchRuns = person.search_runs || []
  const resultCounts = person.result_counts || {
    confirmed: person.results?.length || 0,
    open: reviewCandidates.length,
    rejected: 0,
    not_found: 0,
    error: 0,
  }

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="avatar" style={{ background: color, width: 48, height: 48, fontSize: '1.1rem' }}>
            {initials(person.first_name, person.last_name)}
          </div>
          <div>
            <div className="modal-title">{person.first_name} {person.last_name}</div>
            <div className="modal-subtitle">
              {person.nickname && `"${person.nickname}" · `}
              {person.city && `${person.city} · `}
              {formatDate(person.created_at)}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="modal-body">
          {actionError && <div className="notice notice-error">{actionError}</div>}
          {copyNotice && <div className="copy-toast">{copyNotice}</div>}

          <div className="detail-tabs" role="tablist" aria-label="Case Ansicht">
            <DetailTab id="profile" active={activeDetailTab} setActive={setActiveDetailTab} icon={<UserRound size={13} />} label="Profil" />
            <DetailTab id="results" active={activeDetailTab} setActive={setActiveDetailTab} icon={<ListChecks size={13} />} label="Treffer" />
            <DetailTab id="research" active={activeDetailTab} setActive={setActiveDetailTab} icon={<Link2 size={13} />} label="Research" />
            <DetailTab id="history" active={activeDetailTab} setActive={setActiveDetailTab} icon={<History size={13} />} label="Historie" />
          </div>

          {activeDetailTab === 'profile' && (
            <>
              <div className="section">
                <div className="section-title">Status</div>
                <div className="status-row">
                  {STATUSES.map(s => (
                    <button
                      key={s.key}
                      className={`status-btn badge-${s.key} ${person.status === s.key ? 'active' : ''}`}
                      onClick={() => handleStatusChange(s.key)}
                      disabled={searching}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <CandidateSummary counts={resultCounts} />

              <ProfileInfoSection
                person={person}
                form={editForm}
                setForm={setEditForm}
                editing={editingProfile}
                saving={savingProfile}
                onEdit={() => setEditingProfile(true)}
                onCancel={handleProfileCancel}
                onSave={handleProfileSave}
              />

              <div className="section">
                <div className="section-title">Generierte Benutzernamen</div>
                <div className="chip-list">
                  {usernames.map(u => <span key={u} className="chip">{u}</span>)}
                </div>
              </div>

              <SearchSection
                searching={searching}
                usernames={usernames}
                searchSummary={searchSummary}
                onSearch={handleSearch}
              />
            </>
          )}

          {activeDetailTab === 'results' && (
            <ResultsPanel
              person={person}
              reviewCandidates={reviewCandidates}
              visibleReviewCandidates={visibleReviewCandidates}
              reviewVisibleCount={reviewVisibleCount}
              setReviewVisibleCount={setReviewVisibleCount}
              candidateAction={candidateAction}
              onCandidateAction={handleCandidateAction}
            />
          )}

          {activeDetailTab === 'research' && (
            <ResearchPanel
              tasksPayload={researchTasks}
              fallbackBundle={researchLinks}
              showSensitive={showSensitiveSources}
              setShowSensitive={setShowSensitiveSources}
              onCopy={handleCopy}
              onSync={handleResearchSync}
              onTaskAction={handleResearchTaskAction}
              acting={researchAction}
            />
          )}

          {activeDetailTab === 'history' && (
            <HistoryPanel runs={searchRuns} counts={resultCounts} />
          )}

          {/* Delete */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.2rem', paddingTop: '0.8rem', borderTop: '1px solid var(--border)' }}>
            <button className="btn btn-danger btn-sm" onClick={handleDelete}>
              <Trash2 size={12} /> Löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function DetailTab({ id, active, setActive, icon, label }) {
  return (
    <button
      className={`detail-tab ${active === id ? 'active' : ''}`}
      onClick={() => setActive(id)}
      type="button"
    >
      {icon}
      {label}
    </button>
  )
}

function CandidateSummary({ counts }) {
  const items = [
    ['Bestätigt', counts.confirmed || 0],
    ['Offen', counts.open || 0],
    ['Abgelehnt', counts.rejected || 0],
    ['Fehler', counts.error || 0],
  ]

  return (
    <div className="case-summary">
      {items.map(([label, value]) => (
        <div key={label} className="case-summary-item">
          <span className="case-summary-num">{value}</span>
          <span className="case-summary-label">{label}</span>
        </div>
      ))}
    </div>
  )
}

function ProfileInfoSection({ person, form, setForm, editing, saving, onEdit, onCancel, onSave }) {
  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }))
  const contactFields = [
    ['email', 'E-Mail'],
    ['phone', 'Telefon'],
    ['city', 'Stadt'],
  ]
  const contextFields = [
    ['maiden_name', 'Früherer Name'],
    ['old_city', 'Früherer Ort'],
    ['school', 'Schule'],
    ['employer', 'Arbeitgeber'],
    ['association', 'Verein/Event'],
    ['year_hint', 'Jahrgang/Jahr'],
  ]

  if (editing) {
    return (
      <div className="section">
        <div className="section-heading-row">
          <div className="section-title">Profil und Hinweise bearbeiten</div>
        </div>
        <div className="form-grid compact-form-grid">
          <TextInput label="Vorname *" value={form.first_name} onChange={(value) => setField('first_name', value)} />
          <TextInput label="Nachname *" value={form.last_name} onChange={(value) => setField('last_name', value)} />
          <TextInput label="Spitzname" value={form.nickname} onChange={(value) => setField('nickname', value)} />
          <TextInput label="E-Mail" value={form.email} onChange={(value) => setField('email', value)} />
          <TextInput label="Telefon" value={form.phone} onChange={(value) => setField('phone', value)} />
          <TextInput label="Stadt" value={form.city} onChange={(value) => setField('city', value)} />
          <TextInput label="Früherer Name" value={form.maiden_name} onChange={(value) => setField('maiden_name', value)} />
          <TextInput label="Früherer Ort" value={form.old_city} onChange={(value) => setField('old_city', value)} />
          <TextInput label="Schule" value={form.school} onChange={(value) => setField('school', value)} />
          <TextInput label="Arbeitgeber" value={form.employer} onChange={(value) => setField('employer', value)} />
          <TextInput label="Verein/Event" value={form.association} onChange={(value) => setField('association', value)} />
          <TextInput label="Jahrgang/Jahr" value={form.year_hint} onChange={(value) => setField('year_hint', value)} />
          <div className="form-group wide">
            <label className="form-label">Notizen</label>
            <textarea
              className="form-textarea"
              value={form.notes || ''}
              onChange={(event) => setField('notes', event.target.value)}
            />
          </div>
        </div>
        <div className="form-actions">
          <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={saving} type="button">
            <X size={12} /> Abbrechen
          </button>
          <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving} type="button">
            {saving ? <span className="spinner" /> : <Save size={12} />}
            Speichern
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="section">
      <div className="section-heading-row">
        <div className="section-title">Bekannte Infos</div>
        <button className="btn btn-ghost btn-sm" onClick={onEdit} type="button">
          <Pencil size={12} /> Bearbeiten
        </button>
      </div>
      <div className="detail-grid">
        {contactFields.map(([key, label]) => (
          <div key={key} className="detail-item"><strong>{label}:</strong> {person[key] || '—'}</div>
        ))}
      </div>
      <div className="context-clues">
        <div className="context-clues-title">Hinweise</div>
        <div className="detail-grid">
          {contextFields.map(([key, label]) => (
            <div key={key} className="detail-item"><strong>{label}:</strong> {person[key] || '—'}</div>
          ))}
        </div>
      </div>
      {person.notes && (
        <div className="detail-item context-notes">
          <strong>Notizen:</strong> {person.notes}
        </div>
      )}
    </div>
  )
}

function TextInput({ label, value, onChange }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input
        className="form-input"
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

function SearchSection({ searching, usernames, searchSummary, onSearch }) {
  return (
    <div className="section">
      <div className="section-title">OSINT-Suche</div>
      {searching && (
        <div className="searching">
          <span className="spinner" />
          Durchsuche Plattformkatalog mit {usernames.length} Benutzernamen...
        </div>
      )}
      {!searching && (
        <button className="btn btn-primary" onClick={onSearch}>
          <Search size={14} /> Suche starten
        </button>
      )}
      {searchSummary && (
        <div className="search-summary">
          {summaryLine(searchSummary)}
          {searchSummary.run_id && <span className="search-run-id">Run #{searchSummary.run_id}</span>}
        </div>
      )}
    </div>
  )
}

function ResultsPanel({
  person,
  reviewCandidates,
  visibleReviewCandidates,
  reviewVisibleCount,
  setReviewVisibleCount,
  candidateAction,
  onCandidateAction,
}) {
  return (
    <>
      {person.results?.length > 0 && (
        <div className="section">
          <div className="section-title">Wahrscheinliche Treffer ({person.results.length})</div>
          {person.results.map(r => (
            <ResultItem key={r.id} result={r} />
          ))}
        </div>
      )}

      {person.results?.length === 0 && reviewCandidates.length === 0 && (
        <div className="empty compact-empty">
          <p>Noch keine Treffer oder prüfbaren Kandidaten.</p>
        </div>
      )}

      {reviewCandidates.length > 0 && (
        <div className="section">
          <div className="section-title">Kandidaten prüfen ({reviewCandidates.length})</div>
          {visibleReviewCandidates.map(r => (
            <div key={r.id} className="result-item result-item-review">
              <span className="result-platform">{r.platform}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <a className="result-link" href={r.profile_url} target="_blank" rel="noopener">
                  {r.profile_url} <ExternalLink size={10} style={{ verticalAlign: 'middle' }} />
                </a>
                <ResultMeta result={r} />
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => onCandidateAction(r.id, 'confirm')}
                disabled={candidateAction.startsWith(`${r.id}:`)}
              >
                {candidateAction === `${r.id}:confirm` ? <span className="spinner" /> : <CheckCircle2 size={12} />}
                Bestätigen
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => onCandidateAction(r.id, 'reject')}
                disabled={candidateAction.startsWith(`${r.id}:`)}
              >
                {candidateAction === `${r.id}:reject` ? <span className="spinner" /> : <XCircle size={12} />}
                Ablehnen
              </button>
            </div>
          ))}
          {reviewCandidates.length > visibleReviewCandidates.length && (
            <button
              className="btn btn-ghost review-more"
              onClick={() => setReviewVisibleCount(reviewVisibleCount + 24)}
            >
              {reviewCandidates.length - visibleReviewCandidates.length} weitere anzeigen
            </button>
          )}
        </div>
      )}
    </>
  )
}

function ResearchPanel({ tasksPayload, fallbackBundle, showSensitive, setShowSensitive, onCopy, onSync, onTaskAction, acting }) {
  const [statusFilter, setStatusFilter] = useState('open')
  const [riskFilter, setRiskFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const tasks = tasksPayload.items || []
  const counts = tasksPayload.counts || {}
  const fallbackSensitive = fallbackBundle?.summary?.sensitive || 0
  const sensitiveCount = counts.sensitive || fallbackSensitive
  const visibleTasks = tasks
    .filter((task) => showSensitive || task.risk !== 'sensitive')
    .filter((task) => statusFilter === 'all' || task.status === statusFilter)
    .filter((task) => !riskFilter || task.risk === riskFilter)
    .filter((task) => !typeFilter || task.type === typeFilter)
  const typeOptions = Object.keys(tasksPayload.by_type || {})
    .filter((type) => tasksPayload.by_type[type] > 0)
    .sort((a, b) => researchTypeLabel(a).localeCompare(researchTypeLabel(b), 'de'))

  return (
    <>
      <div className="case-toolbar">
        <div className="risk-chips">
          <span className="risk-chip risk-low">Low {counts.low || 0}</span>
          <span className="risk-chip risk-medium">Medium {counts.medium || 0}</span>
          <span className="risk-chip risk-sensitive">Sensitive {sensitiveCount}</span>
          <span className="risk-chip risk-low">Offen {counts.open || 0}</span>
          <span className="risk-chip risk-medium">Hits {counts.hit || 0}</span>
        </div>
        <div className="research-toolbar-actions">
          <button
            className="btn btn-ghost btn-sm"
            onClick={onSync}
            disabled={acting === 'sync'}
            type="button"
          >
            {acting === 'sync' ? <span className="spinner" /> : <History size={12} />}
            Aufgaben aktualisieren
          </button>
          {sensitiveCount > 0 && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowSensitive(!showSensitive)}
              type="button"
            >
              {showSensitive ? 'Sensible ausblenden' : 'Sensible anzeigen'}
            </button>
          )}
        </div>
      </div>

      {tasks.length > 0 && (
        <div className="research-filters">
          <div className="review-tabs research-status-tabs" role="tablist" aria-label="Research Status">
            {['open', 'checked', 'hit', 'irrelevant', 'all'].map((status) => (
              <button
                key={status}
                className={`review-tab ${statusFilter === status ? 'active' : ''}`}
                onClick={() => setStatusFilter(status)}
                type="button"
              >
                <span>{researchStatusLabel(status)}</span>
                <span className="review-tab-count">{status === 'all' ? counts.total || 0 : counts[status] || 0}</span>
              </button>
            ))}
          </div>
          <select className="form-select" value={riskFilter} onChange={(event) => setRiskFilter(event.target.value)}>
            <option value="">Alle Risiken</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="sensitive">Sensibel</option>
          </select>
          <select className="form-select" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
            <option value="">Alle Typen</option>
            {typeOptions.map((type) => (
              <option key={type} value={type}>{researchTypeLabel(type)}</option>
            ))}
          </select>
        </div>
      )}

      {tasks.length === 0 && (
        <div className="empty compact-empty">
          <p>Noch keine Research-Aufgaben gespeichert.</p>
          <button className="btn btn-primary btn-sm" onClick={onSync} disabled={acting === 'sync'} type="button">
            {acting === 'sync' ? <span className="spinner" /> : <History size={12} />}
            Aufgaben erzeugen
          </button>
        </div>
      )}

      {tasks.length > 0 && visibleTasks.length === 0 && (
        <div className="empty compact-empty">
          <p>Keine Aufgaben im aktuellen Filter.</p>
        </div>
      )}

      {visibleTasks.length > 0 && (
        <div className="section">
          <div className="section-title">Research-Aufgaben ({visibleTasks.length})</div>
          <div className="research-list">
            {visibleTasks.map((task) => (
              <ResearchTask
                key={task.id}
                task={task}
                onCopy={onCopy}
                onTaskAction={onTaskAction}
                acting={acting}
              />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

function ResearchTask({ task, onCopy, onTaskAction, acting }) {
  const actionPrefix = `${task.id}:`
  const isActing = acting.startsWith(actionPrefix)
  const hitAction = task.type === 'platform_direct' ? 'confirm_result' : 'hit'

  return (
    <div className={`research-row research-task research-task-${task.status} ${task.is_stale ? 'research-task-stale' : ''}`}>
      <div className="research-main">
        <div className="research-title">
          <span>{task.label}</span>
          <span className={`risk-chip risk-${task.risk}`}>{riskLabel(task.risk)}</span>
          <span className="research-status-pill">{researchStatusLabel(task.status)}</span>
          <span className="priority-pill">P{task.priority}</span>
          {task.is_stale && <span className="research-status-pill">veraltet</span>}
        </div>
        <div className="research-query">
          {researchTypeLabel(task.type)} · {task.group_name}
          {task.query && <> · {task.query}</>}
        </div>
      </div>
      <div className="research-actions">
        <button className="btn btn-ghost btn-sm" onClick={() => onCopy(task.query || task.url)} type="button">
          <Copy size={12} />
          Kopieren
        </button>
        <a className="btn btn-ghost btn-sm" href={task.url} target="_blank" rel="noopener">
          <ExternalLink size={12} />
          Öffnen
        </a>
        {task.status !== 'checked' && (
          <button className="btn btn-ghost btn-sm" onClick={() => onTaskAction(task.id, 'checked')} disabled={isActing} type="button">
            {acting === `${task.id}:checked` ? <span className="spinner" /> : <CheckCircle2 size={12} />}
            Geprüft
          </button>
        )}
        {task.status !== 'hit' && (
          <button className="btn btn-primary btn-sm" onClick={() => onTaskAction(task.id, hitAction)} disabled={isActing} type="button">
            {acting === `${task.id}:${hitAction}` ? <span className="spinner" /> : <CheckCircle2 size={12} />}
            Treffer
          </button>
        )}
        {task.status !== 'irrelevant' && (
          <button className="btn btn-danger btn-sm" onClick={() => onTaskAction(task.id, 'irrelevant')} disabled={isActing} type="button">
            {acting === `${task.id}:irrelevant` ? <span className="spinner" /> : <XCircle size={12} />}
            Irrelevant
          </button>
        )}
        {task.status !== 'open' && (
          <button className="btn btn-ghost btn-sm" onClick={() => onTaskAction(task.id, 'open')} disabled={isActing} type="button">
            Zurücksetzen
          </button>
        )}
      </div>
    </div>
  )
}

function HistoryPanel({ runs, counts }) {
  return (
    <>
      <CandidateSummary counts={counts} />
      {runs.length === 0 && (
        <div className="empty compact-empty">
          <p>Noch keine Suchläufe gespeichert.</p>
        </div>
      )}
      {runs.length > 0 && (
        <div className="timeline">
          {runs.map((run) => (
            <div key={run.id} className={`timeline-item timeline-${run.status}`}>
              <div className="timeline-head">
                <strong>Run #{run.id}</strong>
                <span>{formatDateTime(run.finished_at || run.started_at)}</span>
              </div>
              <div className="timeline-meta">
                <span>{run.platform_count || 0} Plattformen</span>
                <span>{run.checks_run || 0} Checks</span>
                <span>{run.summary?.likely || 0} likely</span>
                <span>{run.summary?.manual_review || 0} Kandidaten</span>
                <span>{run.summary?.manual_sources || 0} manuelle Quellen</span>
                <span>{run.preserved?.confirmed || 0} bestätigt erhalten</span>
                <span>{run.preserved?.rejected || 0} abgelehnt erhalten</span>
              </div>
              {run.error && <div className="timeline-error">{run.error}</div>}
            </div>
          ))}
        </div>
      )}
    </>
  )
}

function ResultItem({ result }) {
  return (
    <div className="result-item">
      <span className="result-platform">{result.platform}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <a className="result-link" href={result.profile_url} target="_blank" rel="noopener">
          {result.profile_url} <ExternalLink size={10} style={{ verticalAlign: 'middle' }} />
        </a>
        <ResultMeta result={result} />
      </div>
    </div>
  )
}

function ResultMeta({ result }) {
  return (
    <div className="result-meta">
      {result.username && <span>@{result.username}</span>}
      <span>{result.confidence ?? 0}%</span>
      <span>{statusLabel(result.result_status)}</span>
      {result.evidence && <span>{evidenceLabel(result.evidence)}</span>}
    </div>
  )
}

function summaryLine(payload) {
  const summary = payload.summary || payload
  const preserved = payload.preserved_decisions || {}
  const rejected = (summary.rejected || 0) + (preserved.rejected || 0)

  return `${summary.likely || 0} wahrscheinliche Treffer, `
    + `${summary.maybe || 0} mögliche Treffer, `
    + `${summary.manual_review || 0} Kandidaten zu prüfen, `
    + `${summary.manual_sources || 0} manuelle Quellen, `
    + `${rejected} abgelehnt, `
    + `${summary.not_found || 0} nicht gefunden, `
    + `${summary.error || 0} Fehler.`
}

function statusLabel(status) {
  const labels = {
    likely: 'wahrscheinlich',
    maybe: 'möglich',
    manual_review: 'prüfen',
    not_found: 'nicht gefunden',
    error: 'Fehler',
    rejected: 'abgelehnt',
  }
  return labels[status] || status
}

function researchStatusLabel(status) {
  const labels = {
    open: 'Offen',
    checked: 'Geprüft',
    hit: 'Treffer',
    irrelevant: 'Irrelevant',
    all: 'Alle',
  }
  return labels[status] || status
}

function researchTypeLabel(type) {
  const labels = {
    search_dork: 'Such-Dork',
    name_search: 'Namenssuche',
    username_search: 'Username-Quelle',
    email_research: 'E-Mail-Recherche',
    phone_research: 'Telefon-Recherche',
    platform_direct: 'Profil-Link',
    directory_search: 'Verzeichnis',
  }
  return labels[type] || type
}

function evidenceLabel(evidence) {
  const labels = {
    manual_review_required: 'manuell angelegt',
    api_signal_not_reliable_enough: 'API-Signal unsicher',
    json_missing_match: 'API meldet keinen Treffer',
    manual_confirmed: 'manuell bestätigt',
    manual_rejected: 'manuell abgelehnt',
    manual_reopened: 'wieder geöffnet',
  }

  if (labels[evidence]) return labels[evidence]
  if (evidence.startsWith('json_match:')) return 'API-Match'
  if (evidence.startsWith('missing_status:')) return '404/fehlend'
  if (evidence.startsWith('ambiguous_status:')) return 'uneindeutiger Status'
  return evidence
}

function filterRisk(items, showSensitive) {
  return showSensitive ? items : items.filter((item) => item.risk !== 'sensitive')
}

function riskLabel(risk) {
  const labels = {
    low: 'low',
    medium: 'medium',
    sensitive: 'sensibel',
  }
  return labels[risk] || risk
}

function sourceGroupLabel(key) {
  const labels = {
    name_search: 'Namenssuche',
    username_search: 'Manuelle Username-Quellen',
    email_research: 'E-Mail-Recherche',
    phone_research: 'Telefon-Recherche',
  }
  return labels[key] || key
}

function personToForm(person = {}) {
  return {
    first_name: person.first_name || '',
    last_name: person.last_name || '',
    nickname: person.nickname || '',
    email: person.email || '',
    phone: person.phone || '',
    city: person.city || '',
    maiden_name: person.maiden_name || '',
    old_city: person.old_city || '',
    school: person.school || '',
    employer: person.employer || '',
    association: person.association || '',
    year_hint: person.year_hint || '',
    notes: person.notes || '',
  }
}

function formatDateTime(value) {
  if (!value) return ''
  return new Date(value).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
